import type { LoadingButtonProps } from '@mui/lab/LoadingButton';
import { ApplyModule } from '../../store/features/apply/types';
import { ProgramApplyItem, ScholarshipApplyItem, ScholarshipApplyItemWithProgram, SelectedApplyItem } from './types';
import { ApplyButton } from './ApplyButton';
import React, { useState } from 'react';
import { ApplyDialog } from './ApplyDialog';
import { ChipProps } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetSignedInUser } from '../../hooks/useGetSignedInUser';
import { APP_URLS } from '../../routes/urls';
import ApplyProgramSelect from './ApplyProgramSelect';
import { StrOrNum } from '../../types/util';
import { useApplyForProgramsOrScholarshipsMutation } from '../../store/features/apply/applySlice';
import { BaseQueryError } from '../../store/types';
import _ from 'lodash';
import { theme } from '../../theme';
import { ApplyFeedback } from './ApplyFeedback';
import { ApplyEligibilityFail } from './ApplyEligibilityFail';
import {
  convertApplyItemsToApplyPayload,
  convertApplyItemsToMassApplyItems,
  getParsedEligibilityItems,
  getScholarshipItemWithPrograms,
} from './helper';
import { MassApplyList } from './MassApplyList';
import { PreviewApplyList } from './PreviewApplyList';

interface ApplyProviderRenderPropsPrams {
  renderApplyBtn: (props?: { BtnProps?: LoadingButtonProps; ChipProps?: ChipProps }) => React.ReactElement;
  renderApplyDialog: () => React.ReactElement;
  renderMassApplyList: (children?: React.ReactNode) => React.ReactElement;
}
interface ApplyProviderProps<T extends ApplyModule> {
  module: T;
  applyItems: T extends 'institute_programs'
    ? ProgramApplyItem | ProgramApplyItem[]
    : ScholarshipApplyItem | ScholarshipApplyItem[];
  ApplyBtnProps?: LoadingButtonProps;
  ApplyChipProps?: ChipProps;
  keepMounted?: boolean;
  children?: (renderPropsParams: ApplyProviderRenderPropsPrams) => React.ReactElement;
  MassApplyEmptyComponent?: React.ReactElement;
  MassApplyHeadingComponent?: React.ReactElement;
}

export const ApplyProvider = <T extends ApplyModule = ApplyModule>({
  children,
  module,
  keepMounted,
  ApplyBtnProps,
  ApplyChipProps,
  applyItems,
  MassApplyEmptyComponent,
  MassApplyHeadingComponent,
}: ApplyProviderProps<T>) => {
  const isMassApply = Array.isArray(applyItems);
  const navigate = useNavigate();
  const location = useLocation();
  const { currentData: userData } = useGetSignedInUser();
  const [applyForProgramsOrScholarships, { isError, error, isLoading, isSuccess, data, reset, isUninitialized }] =
    useApplyForProgramsOrScholarshipsMutation(
      isMassApply
        ? undefined
        : {
            fixedCacheKey: `action:apply|module:${module}|id:${applyItems.id}`,
          }
    );
  const [massApplyItems, setMassApplyItems] = useState<SelectedApplyItem[]>(
    convertApplyItemsToMassApplyItems(applyItems)
  );
  const selectedMassApplyItems = massApplyItems.filter((item) => item.isSelected);
  const toggleMassApplyItemSelection = (id: StrOrNum): void => {
    if (isMassApply) {
      setMassApplyItems((prevItems) =>
        prevItems.map((item) => {
          if (item.id.toString() === id.toString()) return { ...item, isSelected: !item.isSelected };
          return item;
        })
      );
    }
  };

  // Scholarship for single scholarship apply
  const [scholarship, setScholarship] = useState<ScholarshipApplyItemWithProgram | null>(
    getScholarshipItemWithPrograms(applyItems, module)
  );

  // Change Program for single scholarship apply
  const changeScholarshipProgram = (programId: StrOrNum | null, scholarshipId?: StrOrNum): void => {
    if (programId) {
      if (isMassApply && scholarshipId) {
        setMassApplyItems((prevItems) =>
          prevItems.map((item) => {
            if (item.id.toString() === scholarshipId.toString()) return { ...item, selectedProgramId: programId };
            return item;
          })
        );
      } else {
        setScholarship((prevValue) => {
          if (prevValue === null) return null;
          return {
            ...prevValue,
            selectedProgramId: programId,
          };
        });
      }
    }
  };

  const _applyForProgramsOrScholarships = (): void => {
    if (userData?.user && !isLoading) {
      const applyPayload = convertApplyItemsToApplyPayload(
        isMassApply ? selectedMassApplyItems : scholarship || applyItems,
        module,
        userData.user.slug,
        userData.user.user_id
      );
      if (applyPayload) applyForProgramsOrScholarships(applyPayload);
    }
  };

  // Apply Dialog State
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const openDialog = (): void => {
    if (!isUninitialized) {
      reset();
    }
    setIsDialogOpen(true);
  };
  const closeDialog = (): void => {
    if (isLoading) return;
    setScholarship((prevValue) => {
      if (prevValue === null) return null;
      return {
        ...prevValue,
        selectedProgramId: undefined,
      };
    });
    setIsDialogOpen(false);
    _.delay(reset, theme.transitions.duration.standard); // This delay is added for transition of modal to finish
  };

  // Render Apply Btn or UI based on applicable status
  const renderApplyBtn: ApplyProviderRenderPropsPrams['renderApplyBtn'] = (props) => {
    const applyItem = !Array.isArray(applyItems) ? applyItems : undefined;
    const _openDialogOrApply = (event: React.MouseEvent): void => {
      event.stopPropagation();
      event.preventDefault();
      if (userData?.user) {
        _applyForProgramsOrScholarships();
        openDialog();
      } else {
        navigate({ pathname: APP_URLS.signIn }, { state: { from: location } });
      }
    };
    return (
      <ApplyButton
        ChipProps={ApplyChipProps || props?.ChipProps}
        {...ApplyBtnProps}
        {...props?.BtnProps}
        loading={isLoading}
        onClick={_openDialogOrApply}
        module={module}
        applyItem={applyItem}
      />
    );
  };

  // Render Dialog Component
  const renderApplyDialog: ApplyProviderRenderPropsPrams['renderApplyDialog'] = () => {
    const eligibilityItems = getParsedEligibilityItems(
      isMassApply ? selectedMassApplyItems : scholarship || applyItems
    );

    const isInUIState = isLoading || isError || isSuccess;

    return (
      <ApplyDialog handleClose={closeDialog} open={isDialogOpen}>
        <ApplyFeedback
          data={data}
          error={error as BaseQueryError | undefined}
          isError={isError}
          isLoading={isLoading}
          isSuccess={isSuccess}
          onCloseBtnClick={closeDialog}
          onRetry={_applyForProgramsOrScholarships}
          eligibilityItems={eligibilityItems}
        />
        <ApplyEligibilityFail
          data={data}
          eligibilityItems={eligibilityItems}
          isSuccess={isSuccess}
          module={module}
          onCloseBtnClick={closeDialog}
        />
        {!isInUIState && !scholarship && massApplyItems.length > 0 && (
          <PreviewApplyList
            module={module}
            onCloseBtnClick={closeDialog}
            onRemove={toggleMassApplyItemSelection}
            renderApplyBtn={renderApplyBtn}
            selectedItems={selectedMassApplyItems}
          />
        )}
        {!isInUIState && scholarship && (
          <ApplyProgramSelect
            onApply={_applyForProgramsOrScholarships}
            onChangeProgram={changeScholarshipProgram}
            onCloseBtnClick={closeDialog}
            scholarship={scholarship}
          />
        )}
      </ApplyDialog>
    );
  };

  // Render UI to select and change program for apply
  const renderMassApplyList: ApplyProviderRenderPropsPrams['renderMassApplyList'] = (children) => {
    return (
      <MassApplyList
        MassApplyEmptyComponent={MassApplyEmptyComponent}
        MassApplyHeadingComponent={MassApplyHeadingComponent}
        onProgramChange={changeScholarshipProgram}
        module={module}
        applyItems={massApplyItems}
        toggleSelect={toggleMassApplyItemSelection}
        renderApplyBtn={() =>
          renderApplyBtn({ BtnProps: { size: 'small', disabled: selectedMassApplyItems.length === 0 } })
        }
        PreviewButtonProps={{ disabled: selectedMassApplyItems.length === 0 || isLoading, onClick: openDialog }}
      >
        {children}
      </MassApplyList>
    );
  };

  const renderChildren = (): React.ReactNode => {
    if (children) {
      return children({
        renderApplyBtn,
        renderApplyDialog,
        renderMassApplyList,
      });
    }
    return null;
  };

  if (keepMounted) {
    return Array.isArray(applyItems) ? (
      renderMassApplyList(renderApplyDialog())
    ) : (
      <>
        {renderApplyBtn()}
        {renderApplyDialog()}
        {renderChildren()}
      </>
    );
  }

  return <>{renderChildren()}</>;
};
